import SearchBar from "components/base/SearchBar";
import { useState } from "react";
import { useSplit } from "./SplitContext";
import {
  getCartonCount,
  getCbm,
  matchesProduct,
  MAX_CARTON_COUNT,
} from "./utils";

function ShipmentRow({ row }) {
  const {
    selectedRowIds,
    selectedCartonCounts,
    remainingCartonCounts,
    toggleRow,
    updateSelectedCartonCount,
  } = useSplit();
  const cartonCount = getCartonCount(row);
  const remainingCartonCount = remainingCartonCounts.get(row.id);
  const qtyPerCartonIsMissing = Number(row.qtyPerCarton) <= 0;
  const invalidCartons = cartonCount === null;
  const selected = selectedRowIds.includes(row.id);
  const selectedCartonCount = selectedCartonCounts[row.id] ?? 0;
  const maxSelectableCartonCount = Math.min(
    remainingCartonCount,
    MAX_CARTON_COUNT,
  );
  const selectedCartonCountIsInvalid =
    selected &&
    (!Number.isInteger(selectedCartonCount) ||
      selectedCartonCount < 1 ||
      selectedCartonCount > maxSelectableCartonCount);
  const remainingQty = invalidCartons
    ? Number(row.qty)
    : remainingCartonCount * Number(row.qtyPerCarton);
  const remainingCbm = invalidCartons
    ? Number(row.totalCbm)
    : getCbm(row, remainingCartonCount);

  function normalizeSelectedCartonCount() {
    if (!selectedCartonCountIsInvalid) return;
    updateSelectedCartonCount(
      row.id,
      Number.isInteger(selectedCartonCount) && selectedCartonCount > 0
        ? maxSelectableCartonCount
        : 1,
    );
  }

  function updateCartonCount(event) {
    const cartonCount = Number(event.target.value);
    if (cartonCount > MAX_CARTON_COUNT) return;
    updateSelectedCartonCount(row.id, cartonCount);
  }

  return (
    <label
      className={`flex items-start gap-3 p-4 font-normal transition-colors ${
        invalidCartons
          ? "cursor-not-allowed"
          : "cursor-pointer opacity-100 hover:bg-gray-50 dark:hover:bg-gray-800"
      }`}
    >
      <input
        className="mt-0.5"
        type="checkbox"
        checked={selected}
        disabled={invalidCartons}
        onChange={() => toggleRow(row)}
      />
      <div
        className={`grid min-w-0 flex-1 items-start gap-x-4 gap-y-2 text-sm ${
          selected
            ? "grid-cols-[8rem_minmax(0,1fr)_auto]"
            : "grid-cols-[8rem_minmax(0,1fr)]"
        }`}
      >
        <div className="col-start-1 row-start-1 break-all font-bold">
          {row.itemNumber}
        </div>
        <div className="col-start-2 row-start-1 min-w-0 break-words">
          {row.productName}
        </div>
        {selected ? (
          <div className="col-start-3 row-start-1 flex items-center justify-end gap-2">
            <input
              aria-invalid={selectedCartonCountIsInvalid}
              aria-label={`Cartons for ${row.itemNumber}, from 1 to ${maxSelectableCartonCount}`}
              className={`w-16 rounded border bg-white px-2 py-1 text-right font-bold dark:bg-gray-800 ${
                selectedCartonCountIsInvalid
                  ? "border-red-500 text-red-700 dark:border-red-400 dark:text-red-300"
                  : "border-gray-300 dark:border-gray-600"
              }`}
              min="1"
              max={maxSelectableCartonCount}
              type="number"
              value={selectedCartonCount}
              onChange={updateCartonCount}
              onBlur={normalizeSelectedCartonCount}
              onClick={(event) => event.stopPropagation()}
            />
            <span>cartons</span>
          </div>
        ) : null}
        <div className="col-start-1 row-start-2">
          <span className="font-bold">{remainingCbm.toFixed(3)}</span> m³
        </div>
        <div className="col-start-2 row-start-2">
          {invalidCartons ? (
            <span className="text-red-600 dark:text-red-300">
              {qtyPerCartonIsMissing
                ? "Enter Qty / CTN to split this item."
                : "Qty must be divisible by Qty / CTN."}
            </span>
          ) : (
            <>
              <span className="font-bold">{remainingQty}</span> /{" "}
              {row.qtyPerCarton} ={" "}
              <span className="font-bold">{remainingCartonCount}</span> cartons
            </>
          )}
        </div>
        {selected ? (
          <div className="col-start-3 row-start-2 flex justify-end gap-4">
            <span>
              <span className="font-bold text-green-600 dark:text-green-500">
                {getCbm(row, selectedCartonCount).toFixed(3)}
              </span>{" "}
              m³
            </span>
            <span>QTY {selectedCartonCount * Number(row.qtyPerCarton)}</span>
          </div>
        ) : null}
      </div>
    </label>
  );
}

function ShipmentRows({ searchText }) {
  const { shipment, remainingCartonCounts } = useSplit();

  return (
    <div className="space-y-4">
      {shipment.shipmentInvoices.map((shipmentInvoice) => {
        const shipmentInvoiceRows = shipmentInvoice.shipmentInvoiceRows.filter(
          (row) => {
            const cartonCount = getCartonCount(row);
            return (
              (cartonCount === null || remainingCartonCounts.get(row.id) > 0) &&
              matchesProduct(row, searchText)
            );
          },
        );
        if (!shipmentInvoiceRows.length) return null;
        const sourceType =
          shipmentInvoice.invoice.odooModelType === "transfer" ? "INT" : "SO";

        return (
          <section
            key={shipmentInvoice.id}
            className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
          >
            <div className="flex items-center justify-between bg-gray-100 px-4 py-3 dark:bg-gray-800">
              <div>
                {sourceType}{" "}
                <span className="font-bold">
                  {shipmentInvoice.invoice.number}
                </span>
              </div>
              <div className="text-sm opacity-70">
                {shipmentInvoiceRows.length} products
              </div>
            </div>
            <div className="max-h-[70vh] divide-y divide-gray-100 overflow-y-auto dark:divide-gray-800">
              {shipmentInvoiceRows.map((row) => (
                <ShipmentRow key={row.id} row={row} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default function SourceProducts() {
  const { selectedCbm } = useSplit();
  const [searchText, setSearchText] = useState("");

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-bold">Products to split</h2>
        <div className="text-sm">
          All selected{" "}
          <span className="text-lg font-bold text-green-600 dark:text-green-500">
            {selectedCbm.toFixed(3)}
          </span>{" "}
          m³
        </div>
      </div>
      <SearchBar
        containerClassName="mb-4"
        value={searchText}
        onChange={setSearchText}
        placeholder="Search product number or name"
      />
      <ShipmentRows searchText={searchText} />
    </section>
  );
}
